/** E14.2 Events: Zeitraum, Sonderprovision, Laden-Rabatt auf alles; Neuigkeit und Push an alle Mieter automatisch. */
import { useState, type FormEvent } from "react";
import { Button, Dialog, EmptyState, Notice, Seitenzustand, StatusBadge, TextField } from "../ui";
import { Card, FormGrid, PageHeader, Textarea } from "../ui/Layout";
import { formatTag, tagVon } from "@shared/zeit";
import { useEventAbsagen, useEventAnlegen, useEvents } from "./wachstum-api";

export function Events() {
  const events = useEvents();
  const anlegen = useEventAnlegen();
  const absagen = useEventAbsagen();
  const [offen, setOffen] = useState(false);
  const [form, setForm] = useState({ name: "", text: "", von: tagVon(), bis: tagVon(), provision: "", rabatt: "", neuigkeit: true });
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const heute = tagVon();

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (form.name.trim().length < 2) return setFehler("Bitte einen Namen eingeben.");
    if (form.bis < form.von) return setFehler("Das Ende liegt vor dem Anfang.");
    try {
      const r = await anlegen.mutateAsync({ name: form.name.trim(), text: form.text.trim(), von: form.von, bis: form.bis, provisionProzent: form.provision.trim() === "" ? null : Number(form.provision.replace(",", ".")), rabattProzent: form.rabatt.trim() === "" ? null : Number(form.rabatt), neuigkeit: form.neuigkeit });
      setHinweis(form.neuigkeit ? `Event angelegt. Neuigkeit veröffentlicht, ${r.benachrichtigt} Push-Nachrichten verschickt.` : "Event angelegt.");
      setOffen(false);
      setForm({ name: "", text: "", von: heute, bis: heute, provision: "", rabatt: "", neuigkeit: true });
    } catch (err) {
      setFehler(err instanceof Error ? err.message : "Anlegen hat nicht geklappt.");
    }
  }

  return (
    <>
      <PageHeader kicker="Mieter" title="Events" sub="Flohmarkttag, Late-Night-Shopping: Zeitraum, Sonderprovision und Rabatt des Ladens auf alle Artikel" actions={<Button variant="primary" icon="plus" onClick={() => setOffen(true)}>Event anlegen</Button>} />
      {hinweis ? <Notice tone="success">{hinweis}</Notice> : null}
      <Seitenzustand query={events} istLeer={(d) => d.events.length === 0} leer={<EmptyState illustration="all-done" title="Noch kein Event" action={<Button variant="primary" icon="plus" onClick={() => setOffen(true)}>Erstes Event anlegen</Button>}>Ein Event gilt an der Kasse automatisch: Rabatt und Provision werden je Verkauf im Zeitraum gesetzt.</EmptyState>}>
        {(d) => (
          <Card flush>
            <table className="rg-table">
              <thead><tr><th>Event</th><th>Zeitraum</th><th>Provision</th><th>Rabatt</th><th>Status</th><th><span className="rg-sr">Aktion</span></th></tr></thead>
              <tbody>
                {d.events.map((ev) => {
                  const laeuft = !ev.abgesagtAt && ev.von <= heute && ev.bis >= heute;
                  return (
                    <tr key={ev.id}>
                      <td><span className="sc-strong">{ev.name}</span>{ev.text ? <div className="sc-muted sc-small">{ev.text}</div> : null}</td>
                      <td className="sc-num">{ev.von === ev.bis ? formatTag(ev.von) : `${formatTag(ev.von)} – ${formatTag(ev.bis)}`}</td>
                      <td>{ev.provisionProzent === null ? <span className="sc-muted">wie üblich</span> : `${ev.provisionProzent} %`}</td>
                      <td>{ev.rabattProzent === null ? <span className="sc-muted">keiner</span> : `${ev.rabattProzent} % auf alles`}</td>
                      <td>{ev.abgesagtAt ? <StatusBadge status="neutral">abgesagt</StatusBadge> : laeuft ? <StatusBadge status="paid">läuft</StatusBadge> : ev.bis < heute ? <StatusBadge status="neutral">vorbei</StatusBadge> : <StatusBadge status="wait">geplant</StatusBadge>}</td>
                      <td>{!ev.abgesagtAt && ev.bis >= heute ? <Button variant="quiet" icon="x" onClick={() => { if (window.confirm(`${ev.name} absagen? Die Neuigkeit wird entfernt.`)) absagen.mutate(ev.id); }}>Absagen</Button> : null}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}
      </Seitenzustand>
      <Dialog open={offen} title="Event anlegen" onClose={() => setOffen(false)} actions={<><Button variant="quiet" onClick={() => setOffen(false)}>Abbrechen</Button><Button variant="primary" type="submit" form="event-form" loading={anlegen.isPending}>Anlegen</Button></>}>
        <form id="event-form" className="app-form" onSubmit={(e) => void absenden(e)} noValidate>
          {fehler ? <Notice tone="error">{fehler}</Notice> : null}
          <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z. B. Flohmarkttag" autoFocus maxLength={80} />
          <Textarea label="Text für Mieter (optional)" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={3} maxLength={1000} />
          <FormGrid>
            <TextField label="Von" type="date" value={form.von} onChange={(e) => setForm({ ...form, von: e.target.value })} />
            <TextField label="Bis" type="date" value={form.bis} onChange={(e) => setForm({ ...form, bis: e.target.value })} />
          </FormGrid>
          <FormGrid>
            <TextField label="Sonderprovision" value={form.provision} onChange={(e) => setForm({ ...form, provision: e.target.value })} inputMode="decimal" suffix="%" hint="Leer = wie im Vertrag" />
            <TextField label="Rabatt des Ladens" value={form.rabatt} onChange={(e) => setForm({ ...form, rabatt: e.target.value })} inputMode="numeric" suffix="%" hint="Leer = kein Rabatt; mindert den Verkaufspreis, Anteil des Mieters rechnet sich vom Endpreis" />
          </FormGrid>
          <label className="sc-check"><input type="checkbox" checked={form.neuigkeit} onChange={(e) => setForm({ ...form, neuigkeit: e.target.checked })} /> Als Neuigkeit veröffentlichen und alle Mieter per Push informieren</label>
        </form>
      </Dialog>
    </>
  );
}
